import type {
  VendorWithRelations,
  VendorCategory,
  VendorUsage,
  CreateVendorInput,
  UpdateVendorInput,
  AddVendorCategoryInput,
  AddVendorContactInput,
  UpdateVendorContactInput,
  VendorContact,
  VendorContactPortalAccessInput,
  VendorPaymentMethod,
  VendorPaymentMethodInput,
} from '@peles/shared';
import { apiClient } from './client';

export async function listVendors(filters?: {
  isActive?: boolean;
  city?: string;
  vendorCategoryId?: string;
}): Promise<VendorWithRelations[]> {
  const params = new URLSearchParams();
  if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
  if (filters?.city) params.set('city', filters.city);
  if (filters?.vendorCategoryId) params.set('vendorCategoryId', filters.vendorCategoryId);

  const { data } = await apiClient.get<VendorWithRelations[]>('/vendors', { params });
  return data;
}

export async function getVendor(id: string): Promise<VendorWithRelations> {
  const { data } = await apiClient.get<VendorWithRelations>(`/vendors/${id}`);
  return data;
}

export async function createVendor(input: CreateVendorInput): Promise<VendorWithRelations> {
  const { data } = await apiClient.post<VendorWithRelations>('/vendors', input);
  return data;
}

export async function updateVendor(
  id: string,
  updates: UpdateVendorInput,
): Promise<VendorWithRelations> {
  const { data } = await apiClient.patch<VendorWithRelations>(`/vendors/${id}`, updates);
  return data;
}

export async function deactivateVendor(id: string): Promise<VendorWithRelations> {
  const { data } = await apiClient.delete<VendorWithRelations>(`/vendors/${id}`);
  return data;
}

export async function getVendorUsage(id: string): Promise<VendorUsage> {
  const { data } = await apiClient.get<VendorUsage>(`/vendors/${id}/usage`);
  return data;
}

export async function deleteVendor(id: string, reassignToVendorId: string | null): Promise<void> {
  await apiClient.post(`/vendors/${id}/delete`, { reassignToVendorId });
}

export async function addVendorCategory(
  vendorId: string,
  input: AddVendorCategoryInput,
): Promise<VendorWithRelations> {
  const { data } = await apiClient.post<VendorWithRelations>(
    `/vendors/${vendorId}/categories`,
    input,
  );
  return data;
}

export async function removeVendorCategory(vendorId: string, categoryId: string): Promise<void> {
  await apiClient.delete(`/vendors/${vendorId}/categories/${categoryId}`);
}

export async function addVendorContact(
  vendorId: string,
  input: AddVendorContactInput,
): Promise<VendorContact> {
  const { data } = await apiClient.post<VendorContact>(`/vendors/${vendorId}/contacts`, input);
  return data;
}

/** A contact's role, or making them the vendor's primary contact. */
export async function updateVendorContact(
  vendorId: string,
  userId: string,
  input: UpdateVendorContactInput,
): Promise<VendorContact> {
  const { data } = await apiClient.patch<VendorContact>(
    `/vendors/${vendorId}/contacts/${userId}`,
    input,
  );
  return data;
}

/** May this contact sign in to the vendor portal; `notify` emails them that they can. */
export async function setVendorContactPortalAccess(
  vendorId: string,
  userId: string,
  input: VendorContactPortalAccessInput,
): Promise<VendorContact> {
  const { data } = await apiClient.put<VendorContact>(
    `/vendors/${vendorId}/contacts/${userId}/portal-access`,
    input,
  );
  return data;
}

export async function removeVendorContact(vendorId: string, userId: string): Promise<void> {
  await apiClient.delete(`/vendors/${vendorId}/contacts/${userId}`);
}

export async function listVendorCategories(): Promise<VendorCategory[]> {
  const { data } = await apiClient.get<VendorCategory[]>('/vendor-categories');
  return data;
}

export async function addVendorPaymentMethod(
  vendorId: string,
  input: VendorPaymentMethodInput,
): Promise<VendorPaymentMethod> {
  const { data } = await apiClient.post<VendorPaymentMethod>(
    `/vendors/${vendorId}/payment-methods`,
    input,
  );
  return data;
}

export async function updateVendorPaymentMethod(
  vendorId: string,
  methodId: string,
  input: VendorPaymentMethodInput,
): Promise<VendorPaymentMethod> {
  const { data } = await apiClient.put<VendorPaymentMethod>(
    `/vendors/${vendorId}/payment-methods/${methodId}`,
    input,
  );
  return data;
}

/** The bank a routing number belongs to, or null when the bank directory does not know it. */
export async function lookupRoutingNumber(
  routingNumber: string,
): Promise<{ routingNumber: string; bankName: string } | null> {
  try {
    const { data } = await apiClient.get<{ routingNumber: string; bankName: string }>(
      `/vendors/routing-numbers/${encodeURIComponent(routingNumber)}`,
    );
    return data;
  } catch {
    return null;
  }
}

export async function removeVendorPaymentMethod(vendorId: string, methodId: string): Promise<void> {
  await apiClient.delete(`/vendors/${vendorId}/payment-methods/${methodId}`);
}

// ─── Logo ────────────────────────────────────────────────────────────────────

export async function getVendorLogoUploadUrl(
  vendorId: string,
  fileName: string,
  mimeType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  const { data } = await apiClient.post<{ uploadUrl: string; s3Key: string }>(
    `/vendors/${vendorId}/logo/upload-url`,
    { fileName, mimeType },
  );
  return data;
}

export async function setVendorLogo(
  vendorId: string,
  s3Key: string | null,
): Promise<VendorWithRelations> {
  const { data } = await apiClient.put<VendorWithRelations>(`/vendors/${vendorId}/logo`, {
    s3Key,
  });
  return data;
}

export async function deleteVendorLogo(vendorId: string): Promise<VendorWithRelations> {
  const { data } = await apiClient.delete<VendorWithRelations>(`/vendors/${vendorId}/logo`);
  return data;
}
